'use client';

import {clsx} from '@gfiber/typesmith';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  type ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  type ReactNode,
  useContext,
  useId,
  useState,
} from 'react';
import {ArrowDropDownIcon, CheckIcon} from '../../../components/Icon/v2';
import {useNewTheme} from '../../../hooks/useNewTheme';
import type {Theme} from '../../../styles/themes';
import {themeV1, themeV2} from '../../../styles/themes';
import {FieldHints} from '../../FieldHints/FieldHints';
import {ThemeProvider} from '../../ThemeProvider/v2';
import styles from './SelectDropDown.module.scss';

export type SelectProps = ComponentPropsWithoutRef<
  typeof SelectPrimitive.Root
> & {
  placeholder?: string;
  label?: string;
  required?: boolean;
  invalid?: boolean;
  helpText?: ReactNode;
  errorText?: ReactNode;
  helpTextClass?: string;
  errorTextClass?: string;
  onChange?: (value: string) => void;
  contentClassName?: string;
  viewportClassName?: string;
  className?: string;
  secondaryBackground?: boolean;
  disabled?: boolean;
  tertiaryBackground?: boolean;
  'data-testid'?: string;
};

const SelectDropdownContext = createContext({handleTabPress: () => {}});

export const SelectDropDown = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      placeholder,
      label,
      required,
      invalid,
      helpText,
      errorText,
      helpTextClass,
      errorTextClass,
      onChange,
      contentClassName,
      viewportClassName,
      className,
      disabled,
      secondaryBackground,
      tertiaryBackground,
      ...props
    },
    forwardedRef,
  ): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const labelId = useId();
    const helpTextId = `${labelId}-help`;
    const errorTextId = `${labelId}-error`;
    const ariaDescribedBy =
      [helpText ? helpTextId : '', invalid && errorText ? errorTextId : '']
        .filter(Boolean)
        .join(' ') || undefined;

    const handleTabPress = () => {
      setIsOpen(false);
    };

    const handleOpenChange = (open: boolean) => {
      setIsOpen(!!open);
    };

    const {applyNewTheme} = useNewTheme();

    const activeTheme: Theme = applyNewTheme ? themeV2 : themeV1;
    const prefix = applyNewTheme ? 'pixel-party-v2' : 'pixel-party-v1';

    const {errorText: _, invalid: __, ...rootProps} = props as any;

    return (
      <ThemeProvider
        options={activeTheme}
        prefix={prefix}
      >
        <div className={styles.wrapper}>
          <div className={clsx(className, styles.selectContainer)}>
            <SelectPrimitive.Root
              {...rootProps}
              defaultValue={props.defaultValue}
              value={props.value}
              onValueChange={(value: string) => onChange?.(value)}
              open={isOpen}
              onOpenChange={handleOpenChange}
              required={required || false}
            >
              <SelectPrimitive.Trigger
                className={clsx(
                  styles.menuTrigger,
                  props.value && styles.highlightColor,
                  isOpen && props.value && styles.openColor,
                  invalid && styles.hasError,
                  disabled && styles.disabled,
                )}
                ref={forwardedRef}
                aria-labelledby={labelId}
                aria-label={label}
                aria-describedby={ariaDescribedBy}
              >
                <label
                  id={labelId}
                  className={clsx(
                    styles.textFieldLabel,
                    secondaryBackground && styles.secondaryBackground,
                    tertiaryBackground && styles.tertiaryBackground,
                  )}
                >
                  {label}
                </label>

                {props.value || props.defaultValue ? (
                  <SelectPrimitive.Value />
                ) : (
                  <span>{!invalid ? placeholder : ''}</span>
                )}

                <SelectPrimitive.Icon className={styles.menuIcon}>
                  <ArrowDropDownIcon />
                </SelectPrimitive.Icon>
              </SelectPrimitive.Trigger>
              <SelectPrimitive.Portal>
                <SelectPrimitive.Content
                  position="popper"
                  className={clsx(styles.menuContent, contentClassName)}
                  sideOffset={props.value ? 5 : 0}
                >
                  <SelectPrimitive.Viewport
                    className={clsx(
                      styles.Viewport,
                      props.value && styles.highlightColor,
                      viewportClassName,
                    )}
                  >
                    <SelectDropdownContext.Provider value={{handleTabPress}}>
                      {children}
                    </SelectDropdownContext.Provider>
                  </SelectPrimitive.Viewport>
                </SelectPrimitive.Content>
              </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
          </div>
          <FieldHints
            helpText={helpText}
            errorText={invalid ? errorText : ''}
            helpTextClass={helpTextClass}
            errorTextClass={errorTextClass}
            helpTextId={helpTextId}
            errorTextId={errorTextId}
          />
        </div>
      </ThemeProvider>
    );
  },
);

export type SelectItemProps = ComponentPropsWithoutRef<
  typeof SelectPrimitive.Item
> & {
  indicatorClassName?: string;
  itemClassName?: string;
  disableDefaultOptionHighlight?: boolean;
  selectedValue?: string;
};

export const SelectItemDropDown = forwardRef<HTMLDivElement, SelectItemProps>(
  (
    {
      children,
      className,
      itemClassName,
      indicatorClassName,
      disableDefaultOptionHighlight,
      selectedValue,
      ...props
    },
    ref,
  ) => {
    const [noArrowKeyPressed, setNoArrowKeyPressed] = useState<boolean>(true);

    const {handleTabPress} = useContext(SelectDropdownContext);

    /**
     * Keyboard Interactions for Enter Key:
     * When focus is on Select.Trigger, opens the select and focuses the first item.
     * When focus is on an item, selects the focused item.
     *
     * @param {boolean} disableDefaultOptionHighlight - controls whether the first item in the select menu receives initial focus when the menu opens
     *
     * @param {string} selectedValue - the currently selected value. This parameter is required when disableDefaultOptionHighlight is set to true.
     *
     */
    const handleKeyChange = (event: React.KeyboardEvent) => {
      if (event.key === 'Tab') {
        handleTabPress();
      }
      if (disableDefaultOptionHighlight) {
        switch (event.key) {
          case 'ArrowUp':
            setNoArrowKeyPressed(false);
            break;

          case 'ArrowDown':
            if (noArrowKeyPressed && selectedValue?.length === 0) {
              event.preventDefault();
            }
            setNoArrowKeyPressed(false);
            break;

          default: {
            break;
          }
        }
      }
    };

    const {applyNewTheme} = useNewTheme();

    const activeTheme: Theme = applyNewTheme ? themeV2 : themeV1;
    const prefix = applyNewTheme ? 'pixel-party-v2' : 'pixel-party-v1';

    return (
      <ThemeProvider
        options={activeTheme}
        prefix={prefix}
      >
        <SelectPrimitive.Item
          {...props}
          className={clsx(
            styles.menuItem,
            noArrowKeyPressed &&
              disableDefaultOptionHighlight &&
              styles.disableOptionHighlight,
            className,
          )}
          ref={ref}
          onKeyDown={e => handleKeyChange(e)}
          onKeyUp={e => handleKeyChange(e)}
        >
          <SelectPrimitive.ItemText className={itemClassName}>
            {children}
          </SelectPrimitive.ItemText>
          <SelectPrimitive.ItemIndicator
            className={clsx(styles.ItemIndicator, indicatorClassName)}
          >
            <CheckIcon className={clsx(styles.iconSize)} />
          </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
      </ThemeProvider>
    );
  },
);
